import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleUp, faAnglesDown, faAnglesUp, faArrowDown, faArrowDownLong, faArrowUp, faArrowUpLong, faEquals, faSave } from "@fortawesome/free-solid-svg-icons";
import { RocketRangeGameInstance, RocketRangeUserBet } from "../../artifacts/ts/types";
import {
    ALPH_TOKEN_ID,
    DUST_AMOUNT,
    ONE_ALPH,
    convertAlphAmountWithDecimals,
    prettifyAttoAlphAmount,
    prettifyTokenAmount,
    web3,
} from "@alephium/web3";
import { useConnect, useWallet } from "@alephium/web3-react";
import { RocketRange } from "src/artifacts/ts";

import { balanceOf, getContractConfig } from "src/services/utils";
import { useAppContext } from "src/context/AppContext";
import RocketRangeUserGuide from "./RocketRangeUserGuide";
import TradingViewWidget from "src/components/tradingview/TradingViewWidget.jsx";
import RocketRangeBar from "./RocketRangeBar";
import { faChartLine } from "@fortawesome/free-solid-svg-icons";


const RocketRangeCurrentRound: React.FC = ({ }) => {
    web3.setCurrentNodeProvider(process.env.REACT_APP_NODE_URL ? process.env.REACT_APP_NODE_URL : "https://wallet.mainnet.alephium.org");
    const { connectionStatus, nodeProvider, account, signer } = useWallet()
    const { connect } = useConnect()
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(true);

    const { showPendingTX } = useAppContext();
    const [gameData, setGameData] = useState<RocketRangeGameInstance>({} as RocketRangeGameInstance);
    const [roundId, setRoundId] = React.useState<bigint>(0n);
    const [selectedOption, setSelectedOption] = React.useState<number | null>(null);
    const [betAmount, setBetAmount] = React.useState<number>(0);
    const [alphLastPrice, setAlphLastPrice] = useState<number>(0);
    const [countdown, setCountdown] = useState<string>("00H:00M");
    const [userBet, setUserBet] = useState<RocketRangeUserBet>();
    const [availableAlph, setAvailableAlph] = useState(0n);
    const [tradingViewVisible, setTradingViewVisible] = useState(false);

    const fetchData = async () => {
        const x = await RocketRange.at(getContractConfig("RocketRange").address).fetchState();
        const roundIdLocal = x.fields.latestRoundId;
        setRoundId(roundIdLocal);
        const res = await RocketRange.at(getContractConfig("RocketRange").address).view.loadGameInstance({ args: { roundId: roundIdLocal } });
        setGameData(res.returns[1]);

        const alphPriceRequest = await fetch('https://api.diadata.org/v1/assetQuotation/Alephium/tgx7VNFoP9DJiFMFgXXtafQZkUvyEdDHT9ryamHJYrjq');
        setAlphLastPrice(Number((await alphPriceRequest.json()).Price))

        // Update the countdown
        const now = Date.now();
        const remainingTime = Math.max(Number(res.returns[1].betCloseTime) - now, 0);
        const hours = Math.floor(remainingTime / (1000 * 60 * 60));
        const minutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60));
        setCountdown(`${hours.toString().padStart(2, "0")}H:${minutes.toString().padStart(2, "0")}M`);


        if (account) {
            const res = await RocketRange.at(getContractConfig("RocketRange").address).view.loadUserBet({ args: { user: account.address } });
            setUserBet(res.returns[1]);
            setAvailableAlph(await balanceOf(ALPH_TOKEN_ID, account.address));
        } else {
            setUserBet(undefined);
        }

        setLoading(false);
    }

    useEffect(() => {
        // Initial fetch
        fetchData();

        // Set up interval to fetch every 8 seconds
        const interval = setInterval(fetchData, 8000);

        // Cleanup interval on unmount
        return () => clearInterval(interval);
    }, [account]);


    const placeBet = (option: number | null, amount: number) => {
        if (!option || amount <= 0) return;
        const alphAmount = convertAlphAmountWithDecimals(amount);
        if (signer && alphAmount) {
            RocketRange.at(getContractConfig("RocketRange").address).transact.placeBet({
                signer,
                attoAlphAmount: alphAmount + ONE_ALPH,
                args: {
                    caller: account.address,
                    amount: alphAmount,
                    betChoice: BigInt(option),
                    roundId: gameData.gameId,
                },
            }).then(tx => showPendingTX(tx.txId));

        }
    };

    const updatePrice = () => {
        if (signer) {
            RocketRange.at(getContractConfig("RocketRange").address).transact.setClosePrice({
                signer,
                attoAlphAmount: DUST_AMOUNT,
                args: {
                    roundId: gameData.gameId,
                },
            }).then(tx => showPendingTX(tx.txId));
        }
    };


    const createRound = () => {
        if (signer) {
            RocketRange.at(getContractConfig("RocketRange").address).transact.createNewRound({
                signer: signer,
                attoAlphAmount: ONE_ALPH,
            }).then(tx => showPendingTX(tx.txId))
        }
    }

    const topupOneAlph = () => {
        if (signer) {
            RocketRange.at(getContractConfig("RocketRange").address).transact.topUpNextRoundPot({
                signer: signer,
                attoAlphAmount: ONE_ALPH * 30n,
                args: {
                    amount: ONE_ALPH * 30n
                }
            }).then(tx => showPendingTX(tx.txId))
        }
    }

    const resultMap = {
        1: "Down 4%+",
        2: "Down 1-4%",
        3: "No Change",
        4: "Up 1-4%",
        5: "Up 4%+",
    };

    if (loading) return <></>
    if (Number(gameData.betCloseTime) < (new Date().getTime())) {
        return (
            <div className="p-6 w-full mx-auto">
                {/* Title */}
                <h2 className="text-2xl font-bold text-white mb-4">Round #{gameData.gameId + 1n + ""}</h2>

                {/* Description */}
                <p className="text-gray-300 mb-6">Round not started</p>

                {/* Button */}
                {account ?
                    <button
                        onClick={createRound}
                        className="px-6 py-2 bg-red-600 text-white font-semibold rounded-md shadow-md hover:bg-red-700 transition duration-300"
                    >
                        Create Round
                    </button>
                    :
                    <p className="text-red-500 text-xl my-6 text-center"><strong>Connect wallet to proceed.</strong></p>
                }
            </div>

        );
    }
    return (
        <div className="p-6 w-full mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h2 className="font-bold text-white text-2xl">Round #{gameData.gameId + ""}</h2>
                <div>
                    <button
                        onClick={setTradingViewVisible.bind(this, !tradingViewVisible)}
                        className="py-2 mr-4"
                    >
                        <FontAwesomeIcon className={`${tradingViewVisible ? "text-blue-500" : "text-gray-400"} text-3xl transition duration-200`} icon={faChartLine} />
                    </button>
                    <RocketRangeUserGuide />
                </div>
            </div>

            <div className={`transition-all duration-300 ease-in-out overflow-hidden ${tradingViewVisible ? 'max-h-[424px] opacity-100' : 'max-h-0 opacity-0'
                }`}>
                <div className="mb-6">
                    <TradingViewWidget />
                </div>
            </div>

            {/* Info Section */}
            <div className="flex justify-between items-start mb-6">
                {/* Left: ALPH Price */}
                <div className="flex items-center space-x-2">
                    <img src="/images/icons/alph.png" alt="ALPH" className="h-12 w-12" />
                    <span className="text-2xl text-gray-200 font-bold">${Math.round(alphLastPrice * 1000) / 1000}</span>
                </div>

                {/* Right: Countdown and Total Pot */}
                <div className="text-right">
                    <p className="text-sm text-gray-400 mb-1">Remaining: <span className="font-bold text-red-500">{countdown}</span></p>
                    <p className="text-sm text-gray-400 mb-1">Pot Size: <span className="font-bold text-green-500">{prettifyAttoAlphAmount(gameData.totalPotSize)} ALPH</span></p>
                </div>
            </div>

            {/* Chart */}
            <div className="mb-6">
                <RocketRangeBar gameData={gameData} />
            </div>

            {/* Betting Form */}
            {userBet && userBet.liveRoundId == roundId && userBet.liveRoundBetOneChoice != 0n && userBet.liveRoundBetTwoChoice != 0n ? (
                <div className="mb-6 text-center">
                    <p className="text-gray-400">
                        You have already placed 2 predictions in this round. Good luck!
                    </p>
                </div>
            ) : (
                account ?
                    <div className="mb-6 space-y-4">
                        {/* Line 1: Radio Buttons for Options */}
                        <div className="flex items-center justify-between bg-gray-800 p-2 rounded-lg shadow-md">
                            {[
                                { label: "Down 4%+", value: 1, icon: faAnglesDown, color: "text-red-500" },
                                { label: "Down 1-4%", value: 2, icon: faAngleDown, color: "text-red-400" },
                                { label: "No Change", value: 3, icon: faEquals, color: "text-yellow-500" },
                                { label: "Up 1-4%", value: 4, icon: faAngleUp, color: "text-green-400" },
                                { label: "Up 4%+", value: 5, icon: faAnglesUp, color: "text-green-500" },
                            ].map((option) => (
                                <button
                                    key={option.value}
                                    onClick={() => setSelectedOption(option.value)}
                                    className={`flex-1 px-4 py-2 text-sm font-bold transition rounded-md relative ${option.color} group ${selectedOption === option.value
                                        ? "bg-red-500 shadow text-white"
                                        : "bg-gray-800 hover:bg-gray-700"
                                        }`}
                                >
                                    <FontAwesomeIcon icon={option.icon} className="text-lg" />
                                    {/* Tooltip */}
                                    <span className="absolute w-[100px] top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 bg-black text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition duration-200">
                                        {option.label}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* Line 2: Input for ALPH Amount */}
                        <div>
                            {/* Info Line */}
                            <p className="text-sm text-gray-400 mb-1 text-right">
                                Available: <span className="font-bold text-white">{prettifyAttoAlphAmount(availableAlph)} ALPH</span>
                            </p>

                            {/* Input Field */}
                            <input
                                type="number"
                                placeholder="Enter ALPH Amount"
                                className="w-full px-3 py-2 bg-gray-800 text-gray-300 rounded-md focus:ring focus:ring-red-500"
                                onChange={(e) => setBetAmount(Number(e.target.value))}
                                onKeyDown={(e) => {
                                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                                        e.preventDefault(); // Disable up/down key action
                                    }
                                }}
                            />
                        </div>

                        {/* Line 3: Bet Button */}
                        <div>
                            <button
                                onClick={() => placeBet(selectedOption, betAmount)}
                                disabled={!selectedOption || betAmount <= 0}
                                className="w-full px-4 py-2 bg-red-600 text-white rounded-md font-semibold hover:bg-red-700 disabled:bg-gray-500 disabled:cursor-not-allowed"
                            >
                                Place Prediction
                            </button>
                        </div>
                    </div>
                    :
                    <p className="text-red-500 text-xl my-6 text-center"><strong>Connect wallet to proceed.</strong></p>
            )}

            {/* User Bets */}
            <div>
                <h3 className="text-lg font-bold text-white mb-4">My Predictions</h3>
                {userBet && userBet.liveRoundId == roundId ? (
                    <>
                        <ul className="space-y-2">
                            {userBet.liveRoundBetOneChoice != 0n ? (
                                <li className="flex justify-between items-center p-3 bg-gray-800 rounded-lg shadow-md">
                                    <span className="text-gray-300 font-medium">{resultMap[Number(userBet.liveRoundBetOneChoice)]}</span>
                                    <span className="text-red-400 font-bold">{prettifyAttoAlphAmount(userBet.liveRoundBetOneSize)} ALPH</span>
                                </li>
                            ) : null}
                            {userBet.liveRoundBetTwoChoice != 0n ? (
                                <li className="flex justify-between items-center p-3 bg-gray-800 rounded-lg shadow-md">
                                    <span className="text-gray-300 font-medium">{resultMap[Number(userBet.liveRoundBetTwoChoice)]}</span>
                                    <span className="text-red-400 font-bold">{prettifyAttoAlphAmount(userBet.liveRoundBetTwoSize)} ALPH</span>
                                </li>
                            ) : null}
                        </ul>
                        {/* Message for remaining bets */}
                        {userBet.liveRoundBetOneChoice != 0n && userBet.liveRoundBetTwoChoice == 0n && (
                            <p className="mt-4 text-gray-400">You can place another prediction in this round.</p>
                        )}
                    </>
                ) : (
                    <p className="text-gray-400">
                        No predictions placed yet. You can place up to 2 predictions in this round.
                    </p>
                )}
            </div>

        </div>
    );
};

export default RocketRangeCurrentRound;
